import { supabase } from '../supabase';

export interface Event {
  id: string;
  name: string;
  slug: string;
  duration: number;
  meeting_type: string;
  meeting_link?: string;
  postal_code?: string;
  postal_codes?: string[];
  user_id?: string;
  calendar_id?: string;
  max_bookings_per_slot?: number;
  minimum_scheduling_notice?: number;
  event_type?: string;
}

export async function findEventByPostalCode(postalCode: string): Promise<Event | null> {
  try {
    // First try exact match with the function
    const { data, error } = await supabase
      .rpc('find_event_by_postal_code', { p_postal_code: postalCode });
      
    if (error) {
      console.error('Error finding event by postal code:', error);
      throw error;
    }

    if (data && data.length > 0) {
      return data[0];
    }

    // If no results from function, try direct query
    const { data: directData, error: directError } = await supabase
      .from('events')
      .select(`*`)
      .eq('is_active', true)
      .eq('postal_code', postalCode)
      .maybeSingle();

    if (directError && directError.code !== 'PGRST116') {
      console.error('Error in direct query for postal code:', directError);
      throw directError;
    }

    return directData;
  } catch (error) {
    console.error('Error finding event by postal code:', error);
    return null;
  }
}

export async function checkPostalCodeExists(postalCode: string): Promise<boolean> {
  try {
    // First check with the function
    const { data: funcData, error: funcError } = await supabase
      .rpc('find_event_by_postal_code', { p_postal_code: postalCode });
    
    if (funcError) {
      console.error('Error checking postal code with function:', funcError);
    } else if (funcData && funcData.length > 0) {
      return true;
    }
    
    // Then check with direct query
    const { data, error } = await supabase
      .from('events')
      .select('id, postal_code, postal_codes')
      .eq('is_active', true)
      .or(`postal_code.eq.${postalCode},postal_codes.cs.{${postalCode}}`);
    
    if (error) {
      console.error('Error checking postal code:', error);
      throw error;
    }
    
    // If we found data, return true
    if (data && data.length > 0) {
      return true;
    }
    
    // If no data found, try a more permissive query to debug
    const { data: allEvents, error: allError } = await supabase
      .from('events')
      .select('id, postal_code, postal_codes, is_active');
      
    if (allError) {
      console.error('Error in permissive postal code check:', allError);
    }
    
    return false;
  } catch (error) {
    console.error('Error checking postal code:', error);
    return false;
  }
}

export async function listAllPostalCodes(): Promise<string[]> {
  try {
    // Get all postal codes
    const { data, error } = await supabase
      .from('events')
      .select('postal_code, postal_codes')
      .eq('is_active', true);
    
    if (error) {
      console.error('Error listing postal codes:', error);
      throw error;
    }
    
    // Collect all postal codes (both single and from arrays)
    const postalCodes: string[] = [];
    
    data.forEach(item => {
      // Add single postal code if it exists
      if (item.postal_code) {
        postalCodes.push(item.postal_code);
      }
      
      // Add postal codes from array if they exist
      if (item.postal_codes && Array.isArray(item.postal_codes)) {
        postalCodes.push(...item.postal_codes);
      }
    });
    
    // Filter out null values and return unique postal codes
    const uniquePostalCodes = [...new Set(postalCodes.filter(Boolean))];
    return uniquePostalCodes;
  } catch (error) {
    console.error('Error listing postal codes:', error);
    return [];
  }
}

export async function debugDatabaseAccess(): Promise<any> {
  try {
    // Check if we can access the events table at all
    const { data: eventCount, error: countError } = await supabase
      .from('events')
      .select('id', { count: 'exact', head: true });
    
    if (countError) {
      console.error('Error accessing events table:', countError);
      return { error: countError, message: 'Cannot access events table' };
    }
    
    // Try to get a sample of events
    const { data: sampleEvents, error: sampleError } = await supabase
      .from('events')
      .select('id, name, postal_code, postal_codes, is_active')
      .limit(5);
      
    if (sampleError) {
      console.error('Error getting sample events:', sampleError);
      return { error: sampleError, message: 'Cannot get sample events' };
    }
    
    // Check for specific postal code 10018
    const { data: specificPostal, error: postalError } = await supabase
      .from('events')
      .select('id, name, postal_code, postal_codes, is_active')
      .or(`postal_code.eq.10018,postal_codes.cs.{10018}`);
      
    if (postalError) {
      console.error('Error checking specific postal code:', postalError);
    }
    
    return {
      canAccessEvents: true,
      eventCount: eventCount?.length,
      sampleEvents,
      postalCode10018: specificPostal
    };
  } catch (error) {
    console.error('Error in database access debug:', error);
    return { error, message: 'Exception in database access debug' };
  }
}

/**
 * CLN-6.1 · `checkPostalCodeExistsEnsite` was removed 2026-08-19.
 *
 * It queried `ensite_zip_data` (32,294 rows, NO tenant_id) to decide whether to
 * route a booking through the EnSite integration. That integration is gone: the
 * account behind it is no longer ours, and the component that called it shipped a
 * plaintext credential in the browser bundle.
 *
 * The table itself is deliberately left in place. Dropping it is a data decision,
 * not a code one, and belongs with CLN-4.3's treatment of the Thryve tables.
 */
